import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getGymSession, addGymSet } from '../utils/gymHistory';

const GymView = () => {
  const { t } = useTranslation();
  const [machine, setMachine] = useState('');
  const [reps, setReps] = useState(10);
  const [sessionData, setSessionData] = useState({});
  const [nfcSupported, setNfcSupported] = useState(false);
  const [nfcScanning, setNfcScanning] = useState(false);
  const [nfcDebug, setNfcDebug] = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    setSessionData(getGymSession(today));

    if ('NDEFReader' in window) {
      setNfcSupported(true);
    }
  }, [today]);

  const handleScanNFC = async () => {
    if (!('NDEFReader' in window)) return;
    try {
      setNfcScanning(true);
      // eslint-disable-next-line no-undef
      const ndef = new NDEFReader();
      await ndef.scan();

      ndef.onreading = event => {
        const message = event.message;
        let debugInfo = `Read ${message.records.length} records:\n`;
        for (const record of message.records) {
          debugInfo += `- Type: ${record.recordType}\n`;
          if (record.recordType === "url") {
            try {
              const textDecoder = new TextDecoder(record.encoding);
              const url = textDecoder.decode(record.data);
              debugInfo += `  URL: ${url}\n`;
              // Technogym URL format: http://s.technogym.com/sel/leg_curl
              if (url.includes('technogym.com/sel/')) {
                const scannedMachine = url.split('/').pop();
                if (scannedMachine) {
                  setMachine(scannedMachine);
                }
              }
            } catch (e) {
               debugInfo += `  Error decoding URL: ${e.message}\n`;
            }
          } else if (record.recordType === "text") {
            try {
              const textDecoder = new TextDecoder(record.encoding);
              const text = textDecoder.decode(record.data);
              debugInfo += `  Text: ${text}\n`;
              // Fallback for simple text payloads that might just contain the name
              if (text && !text.startsWith('http')) {
                setMachine(text);
              }
            } catch (e) {
               debugInfo += `  Error decoding Text: ${e.message}\n`;
            }
          } else {
             debugInfo += `  Unhandled type (data length: ${record.data ? record.data.byteLength : 0})\n`;
          }
        }
        setNfcDebug(debugInfo);
        setNfcScanning(false);
      };

      ndef.onreadingerror = (error) => {
        console.error("NFC read error", error);
        setNfcDebug(`Read error: ${error ? error.message : 'Unknown'}`);
        setNfcScanning(false);
      };
    } catch (error) {
      console.error(error);
      setNfcDebug(`Error: ${error ? error.message : 'Unknown'}`);
      setNfcScanning(false);
    }
  };

  const handleAddSet = () => {
    if (!machine) return;
    addGymSet(today, machine, reps);
    setSessionData(getGymSession(today));
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fbeff3', fontFamily: 'Inter, Roboto, Segoe UI, Arial, sans-serif', color: '#333', paddingBottom: 60 }}>
      <div className="pretty-section gym-section">
        <h2 className="pretty-title gym-title">{t('gym')}</h2>

        {nfcSupported && (
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              className="pretty-btn pretty-btn-secondary"
              onClick={handleScanNFC}
              style={{ width: '100%', marginBottom: '0.5rem' }}
            >
              {nfcScanning ? t('scanning_nfc') : t('scan_nfc')}
            </button>
            {nfcDebug && (
              <pre style={{
                background: '#eee',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                marginTop: '0.5rem'
              }}>
                {nfcDebug}
              </pre>
            )}
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{t('machine')}</label>
          <input
            type="text"
            className="pretty-input gym-input"
            value={machine}
            onChange={(e) => setMachine(e.target.value)}
            placeholder={t('machine_placeholder')}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{t('reps')}</label>
          <input
            type="number"
            className="pretty-input gym-input"
            value={reps}
            onChange={(e) => setReps(parseInt(e.target.value, 10))}
            min="1"
          />
        </div>

        <button className="pretty-btn" onClick={handleAddSet} disabled={!machine || reps <= 0}>
          {t('add_set')}
        </button>

        {Object.keys(sessionData).length > 0 && (
          <div className="summary-box">
            <h3 style={{ margin: '0 0 1rem 0', color: '#b85b7b' }}>{t('session_summary')}</h3>
            {Object.entries(sessionData).map(([mach, sets]) => (
              <div key={mach} className="machine-row">
                <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{mach.replace('_', ' ')}</span>
                <span>{sets.join(', ')} ({t('total')}: {sets.reduce((a, b) => a + b, 0)})</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GymView;
